import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import MovingText from "react-moving-text";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from "react-router-dom";

const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    const currentElement = countRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTimestamp;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = timestamp - startTimestamp;
      const percentage = Math.min(progress / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - percentage, 4);

      if (end.toString().includes("+")) {
        const numericEnd = parseInt(end);
        setCount(Math.floor(easeOutQuart * numericEnd));
      } else if (end.toString().includes("M")) {
        const numericEnd = parseFloat(end) * 100;
        const currentValue = (easeOutQuart * numericEnd) / 100;
        setCount(currentValue);
      } else {
        setCount(Math.floor(easeOutQuart * end));
      }

      if (progress < duration) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, [end, duration, isVisible]);

  return { count, countRef };
};

const SliderButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  ${(props) => (props.left ? "left: 20px;" : "right: 20px;")}

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${(props) => (props.left ? "left: 10px;" : "right: 10px;")}
  }
`;

const SlideContainer = styled.div`
  height: 100vh;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 80vh;
  }

  .slick-slider {
    height: 100%;
  }

  .slick-list,
  .slick-track {
    height: 100%;
  }

  .slick-slide {
    height: 100%;
    & > div {
      height: 100%;
    }
  }

  .slick-dots {
    bottom: 25px;
    z-index: 10;

    @media (max-width: 768px) {
      bottom: 15px;
    }

    li button:before {
      color: white;
      opacity: 0.5;
      font-size: 12px;

      @media (max-width: 768px) {
        font-size: 10px;
      }
    }

    li.slick-active button:before {
      color: white;
      opacity: 1;
    }
  }

  .slick-slide {
    transition: all 1s ease-out;
  }

  .slick-track {
    display: flex !important;
  }
`;

const Slide = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
    url(${(props) => props.bgImage}) center/cover no-repeat;
  display: flex !important;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const HeroSection = styled.section`
  height: 100vh;

  @media (max-width: 768px) {
    height: 80vh;
  }

  box-shadow: 0px 21px 24px rgba(0, 0, 0, 0.6);
  position: relative;
  overflow: hidden;
  padding: 0;
  margin: 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  color: ${(props) => props.theme.colors.light};
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  padding: 0 1rem;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const Title = styled.h2`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  margin-top: 4rem;
  margin-bottom: clamp(1rem, 3vw, 1.5rem);
  line-height: 1.2;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const Subtitle = styled.p`
  font-size: clamp(0.75rem, 2vw, ${(props) => props.theme.fontSizes.large});
  margin-bottom: clamp(1.5rem, 4vw, 2rem);
  opacity: 0.8;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
    width: 100%;
  }

  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 1rem 2rem;
  font-size: ${(props) => props.theme.fontSizes.medium};
  font-weight: 600;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  transition: transform 0.3s ease;
  width: auto;

  @media (max-width: 480px) {
    width: 100%;
    padding: 0.8rem 1.5rem;
  }

  background: ${(props) =>
    props.primary ? props.theme.colors.primary : "transparent"};
  color: ${(props) =>
    props.primary ? props.theme.colors.light : props.theme.colors.light};
  border: ${(props) =>
    props.primary ? "none" : "2px solid " + props.theme.colors.light};

  &:hover {
    transform: translateY(-2px);
    background: ${(props) =>
    props.primary
      ? props.theme.colors.secondary
      : "rgba(255, 255, 255, 0.1)"};
  }

  padding: 1rem 2rem;
  font-size: ${(props) => props.theme.fontSizes.medium};
  font-weight: 600;
  border-radius: 5px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const PrimaryButton = styled(Button)`
  background: red;
  color: ${(props) => props.theme.colors.light};
  border: 1px solid red;
  border-radius: 20px;
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  border-radius: 20px;
  color: ${(props) => props.theme.colors.light};
  border: 1px solid ${(props) => props.theme.colors.light};
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: clamp(1.5rem, 4vw, 3rem);
  margin-top: clamp(2rem, 5vw, 3rem);
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const AnimatedNumber = ({ end, suffix = '' }) => {
  const { count, countRef } = useCountUp(end);
  return (
    <div ref={countRef}>
      {typeof count === "number" && count % 1 === 0
        ? Math.floor(count)
        : count.toFixed(1)}
      {suffix}
    </div>
  );
};

const StatItem = styled.div`
  text-align: center;
  flex: 1;
  min-width: 120px;

  @media (max-width: 480px) {
    min-width: 100px;
  }

  h3 {
    font-size: clamp(1.5rem, 3vw, 2.5rem);
    margin-bottom: 0.5rem;
  }

  p {
    font-size: clamp(0.875rem, 1.5vw, 1rem);
    opacity: 0.8;
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: ${(props) => props.theme.fontSizes.medium};
  opacity: 0.9;
`;

const Hero = () => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const beforeChange = (oldIndex, newIndex) => {
    setCurrentSlide(newIndex);
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    fade: false,
    rtl: false,
    cssEase: "cubic-bezier(0.45, 0, 0.55, 1)",
    dotsClass: "slick-dots custom-dots",
    appendDots: (dots) => (
      <div style={{ position: "absolute", bottom: "20px", width: "100%" }}>
        {dots}
      </div>
    ),
    swipe: true,
    touchThreshold: 1000,
    slidesPerRow: 1,
    beforeChange: beforeChange,
  };

  const slides = [
    {
      bgImage: `${process.env.PUBLIC_URL}/images/sliders/MAIN-SLIDE-HBJv3.webp`,
      title: "Transforming Futures in Dar El Khanous",
      subtitle:
        "Through the HeartBeat Journey, we renovated learning spaces, launched a digital classroom, delivered medical and humanitarian support, and brought new hope to students and families in Douar Dar El Khanous.",
      LearnMoreTitle: "Learn More",
      LearnMoreLink: "/heartbeat-journey/project/4",
      showDonate: false
    },
    {
      bgImage: `${process.env.PUBLIC_URL}/images/sliders/imgi_309_PSGE_3.webp`,
      title: 'Our future is being shaped today',
      subtitle: 'Only one month to go until the Plastics Summit - Global Event 2025 in Lisbon, Portugal! 🌍',
      LearnMoreTitle: "Learn More",
      LearnMoreLink: "/events/portugal-event",
      showDonate: false,
      donateTitle: "Learn More",
      donateLink: "/events/portugal-event",
    },
    {
      bgImage: `${process.env.PUBLIC_URL}/images/HeartbeatJourney-Actions/hb-v2/SLIDE_HBJv2_sans_hbjv2.webp`,
      title: "2nd Action Béni Mellal-Khénifra Region",
      subtitle:
        "Our teams are on the ground providing critical aid and support. Your help enables us to reach more communities in need.",
      LearnMoreTitle: "Learn More",
      LearnMoreLink: "/heartbeat-journey/project/3",
      showDonate: true,
      donateTitle: "Support Us",
      donateLink: "/donate",
      stats: [
        { label: "Donors", value: 100, suffix: "+" },
        // { label: "Raised", value: 2.5, suffix: "M" },
        { label: "Actions humanitaire", value: 24, suffix: "+" },
      ]
    }
    // {
    //   bgImage: `${process.env.PUBLIC_URL}/images/photo_5802988364859820038_y.jpg`,
    //   title: "Together We Can Make a Difference",
    //   subtitle:
    //     "Our teams are on the ground providing critical aid and support. Your help enables us to reach more communities in need.",
    //   LearnMoreTitle: "Learn More",
    //   LearnMoreLink: "/campaigns"
    // },
  ];

  let sliderRef = React.useRef(null);

  const next = () => {
    sliderRef.current.slickNext();
  };

  const previous = () => {
    sliderRef.current.slickPrev();
  };

  return (
    <HeroSection>
      <SlideContainer>
        <SliderButton left onClick={previous}>
          &lt;
        </SliderButton>
        <SliderButton onClick={next}>&gt;</SliderButton>
        <Slider ref={sliderRef} {...settings}>
          {slides.map((slide, index) => (
            <Slide key={index} bgImage={slide.bgImage}>
              <Container>
                <HeroContent>
                  <div key={`${index}-${currentSlide}`}>
                    <MovingText
                      type="slideInFromBottom"
                      duration="1000ms"
                      delay="0.32s"
                      direction="normal"
                      timing="ease"
                      iteration="1"
                      fillMode="forwards"
                    >
                      <Title>{slide.title}</Title>
                    </MovingText>

                    <MovingText
                      type="slideInFromTop"
                      duration="1000ms"
                      delay="0.5s"
                      direction="normal"
                      timing="ease"
                      iteration="1"
                      fillMode="forwards"
                    >
                      <Subtitle>{slide.subtitle}</Subtitle>
                    </MovingText>

                    <MovingText
                      type="slideInFromTop"
                      duration="1000ms"
                      delay="0.7s"
                      direction="normal"
                      timing="ease"
                      iteration="1"
                      fillMode="forwards"
                    >
                      <CTAButtons>
                        {slide.showDonate && (
                          <Link to={slide.donateLink || "/donate"}>
                            <PrimaryButton>{slide.donateTitle || "Donate Now"}</PrimaryButton>
                          </Link>
                        )}
                        <Link to={slide.LearnMoreLink}>
                          <SecondaryButton>
                            {slide.LearnMoreTitle}
                          </SecondaryButton>
                        </Link>
                      </CTAButtons>
                    </MovingText>
                  </div>
                  {slide.stats && slide.stats.length > 0 && (
                    <StatsContainer>
                      {slide.stats.map((stat, statIndex) => (
                        <StatItem key={statIndex}>
                          <StatNumber>
                            <AnimatedNumber end={stat.value} suffix={stat.suffix || ""} />
                          </StatNumber>
                          <StatLabel>{stat.label}</StatLabel>
                        </StatItem>
                      ))}
                    </StatsContainer>
                  )}
                </HeroContent>
              </Container>
            </Slide>
          ))}
        </Slider>
      </SlideContainer>
    </HeroSection>
  );
};

export default Hero;
